import { Check, X } from '@tamagui/lucide-icons';
import { Pulse } from '@unistocks/components/animations/pulse';
import { Image } from '@unistocks/components/basic/image';
import { Button } from '@unistocks/components/button/button';
import { Highlighter } from '@unistocks/components/highlighter/highlighter';
import { Center } from '@unistocks/components/layout/center';
import { Column, Row } from '@unistocks/components/layout/flex';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { type SheetProps } from '@unistocks/components/sheet/sheet';
import { Paragraph } from '@unistocks/components/text/text';
import { BankIDMode } from '@unistocks/features/external-accounts/bank-id-mode.enum';
import { ExternalAccountsTermsAndConditions } from '@unistocks/features/external-accounts/terms-and-conditions/terms-and-conditions';
import {
  isBankIdLink,
  redirectToBankID,
  useExternalAccountsAuthenticateCollectMutation
} from '@unistocks/features/external-accounts/utils';
import { t } from '@unistocks/localization';
import {
  AccountProviderDto,
  AuthenticationMethod,
  BankIdStatus,
  ProviderAuthenticateCollectDto
} from '@unistocks/proxy/axios-client';
import { useExternalAccountsAuthenticateInitMutation } from '@unistocks/proxy/axios-client/ExternalAccountsQuery';
import { useIsDarkMode } from '@unistocks/shared/hooks/use-active-color-scheme';
import { Logger } from '@unistocks/shared/services/logger.service';
import { useUnstableGlobalHref } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { QRCode } from 'react-qr-code';
import { Spinner, YGroup, getFontSize, isWeb, useControllableState } from 'tamagui';

const bankIdLight = require('@assets/images/bankid/bankid-light.svg');
const bankIdDark = require('@assets/images/bankid/bankid-dark.svg');

export type SyncAccountModalProps = Omit<SheetProps, 'children'> & {
  provider: AccountProviderDto;
  userIdentifier?: string;
  onAuthenticateSuccess: () => void;
  onAuthenticateError?: (e: any) => void;
};

type BankIdAuthState = {
  qrCode: string | null;
  autoStartToken: string | null;
  status: BankIdStatus | null;
  statusText: string;
};

export const AuthenticateExternalAccountModal: React.FC<SyncAccountModalProps> = ({
  ref,
  open: openProp,
  onOpenChange: onOpenChangeProp,
  provider,
  userIdentifier = null,
  onAuthenticateSuccess,
  onAuthenticateError,
  ...props
}) => {
  const isDarkMode = useIsDarkMode();
  const redirectPath = useUnstableGlobalHref();
  const [bankIdMode, setBankIdMode] = useState<BankIDMode>(
    isWeb ? BankIDMode.OtherDevice : BankIDMode.ThisDevice
  );
  const [bankId, setBankId] = useState<BankIdAuthState>({
    qrCode: null,
    autoStartToken: null,
    status: null,
    statusText: ''
  });
  const themedBankIdLogo = isDarkMode ? bankIdDark : bankIdLight;
  const abortController = useRef<AbortController | null>(null);
  const polling = useRef<boolean>(false);
  const fontSize6 = getFontSize('$6');

  const {
    mutateAsync: authenticateInit,
    isIdle: isAuthenticateInitIdle,
    reset: resetAuthenticateInit
  } = useExternalAccountsAuthenticateInitMutation(provider.name, {
    onMutate: () =>
      setBankId(prev => ({
        ...prev,
        status: BankIdStatus.Pending,
        statusText: t('::ExternalAccounts:Sync:Init')
      })),
    onError: e => {
      setBankId(prev => ({
        ...prev,
        status: BankIdStatus.Failed,
        statusText: t('::ExternalAccounts:Authentication:Error{0}', {
          0: provider.displayName
        })
      }));
      onAuthenticateError?.(e);
    },
    onSuccess: ({ autoStartToken, qrCode }) => {
      Logger.verbose('Authenticate Init', autoStartToken, 'qrCode', qrCode);

      setBankId(prev => ({ ...prev, autoStartToken, qrCode }));
      startPolling();
    }
  });

  const { mutateAsync: authenticateCollect, reset: resetAuthenticateCollect } =
    useExternalAccountsAuthenticateCollectMutation(provider.name, {
      onMutate: () =>
        setBankId(prev => ({ ...prev, statusText: t('::ExternalAccounts:Sync:Collect:BankID') })),
      onSuccess: ({ status, qrCode }) => {
        Logger.verbose('Authenticate Collect', status, 'qrCode', qrCode);

        switch (status) {
          case BankIdStatus.Pending:
            if (qrCode) {
              setBankId(prev => ({
                ...prev,
                qrCode,
                statusText: t('::ExternalAccounts:Sync:Collect:BankID')
              }));
            }
            break;

          case BankIdStatus.Complete:
            setBankId(prev => ({
              ...prev,
              status: BankIdStatus.Complete,
              statusText: t('::ExternalAccounts:Sync:Complete')
            }));

            setTimeout(() => {
              onAuthenticateSuccess();
            }, 1_000);
            break;

          case BankIdStatus.Failed:
            setBankId(prev => ({
              ...prev,
              status: BankIdStatus.Failed,
              statusText: t('::ExternalAccounts:Authentication:Error{0}', {
                0: provider.displayName
              })
            }));
        }
      },
      onError: e => {
        setBankId(prev => ({
          ...prev,
          status: BankIdStatus.Failed,
          statusText: t('::ExternalAccounts:Authentication:Error{0}', {
            0: provider.displayName
          })
        }));
        onAuthenticateError?.(e);
      }
    });

  const switchBankIdMode = () => {
    setBankIdMode(prev =>
      prev === BankIDMode.ThisDevice ? BankIDMode.OtherDevice : BankIDMode.ThisDevice
    );

    if (bankIdMode === BankIDMode.OtherDevice) {
      authenticateInitAndRedirectToBankID();
    } else {
      authenticateInit({
        autostartToken: false,
        providerIdentifier: provider.providerIdentifier,
        userIdentifier,
        redirectUri: null
      });
    }
  };

  const authenticateInitAndRedirectToBankID = async () => {
    const { autoStartToken } = await authenticateInit({
      autostartToken: true,
      providerIdentifier: provider.providerIdentifier,
      userIdentifier,
      redirectUri: null
    });

    if (autoStartToken) {
      redirectToBankID(autoStartToken, redirectPath);
    }
  };

  const startPolling = async () => {
    if (polling.current) return;

    polling.current = true;
    abortController.current?.abort();
    abortController.current = new AbortController();

    while (polling.current) {
      const signal = abortController.current?.signal;

      let res: ProviderAuthenticateCollectDto | null = null;

      try {
        res = await authenticateCollect({
          provider: {
            name: provider.name,
            providerIdentifier: provider.providerIdentifier,
            market: provider.market
          },
          code: null,
          signal
        });
      } catch {
        stopPolling();
      }

      if (res?.status === BankIdStatus.Complete || res?.status === BankIdStatus.Failed) {
        stopPolling();
      }

      await new Promise(resolve => setTimeout(resolve, 2_000));
    }
  };

  const stopPolling = () => {
    abortController.current?.abort();
    polling.current = false;
  };

  const resetState = () => {
    stopPolling();
    setBankId({
      qrCode: null,
      autoStartToken: null,
      status: BankIdStatus.Pending,
      statusText: ''
    });
    setBankIdMode(isWeb ? BankIDMode.OtherDevice : BankIDMode.ThisDevice);
    resetAuthenticateInit();
    resetAuthenticateCollect();
  };

  const onModalOpenChange = (open: boolean) => {
    resetState();
    onOpenChangeProp?.(open);
  };

  const [open, onOpenChange] = useControllableState({
    defaultProp: false,
    prop: openProp,
    onChange: onModalOpenChange
  });

  useEffect(() => {
    if (provider && open) {
      if (provider.authenticationMethod === AuthenticationMethod.BankID && isAuthenticateInitIdle) {
        stopPolling();

        authenticateInit({
          autostartToken: bankIdMode === BankIDMode.ThisDevice,
          providerIdentifier: provider.providerIdentifier,
          userIdentifier,
          redirectUri: null
        });
      } else if (provider.authenticationMethod === AuthenticationMethod.Redirect) {
        onAuthenticateSuccess();
      }
    }
  }, [
    authenticateInit,
    bankIdMode,
    isAuthenticateInitIdle,
    onAuthenticateSuccess,
    open,
    provider,
    userIdentifier
  ]);

  const renderSyncFailed = () => {
    return (
      <Column gap={'$3'}>
        <Center
          transition={'medium'}
          animateOnly={['opacity', 'transform']}
          enterStyle={{
            o: 0,
            y: -20
          }}
          exitStyle={{
            o: 0,
            y: -20
          }}
        >
          <X size={'$10'} strokeWidth={3} color={'$danger'} />
          <Paragraph size={'$6'}>{bankId.statusText}</Paragraph>
        </Center>

        <Button onPress={resetState}>
          <Button.Text>{t('::ExternalAccounts:Sync:TryAgain')}</Button.Text>
        </Button>

        <Button variant={'outlined'} onPress={() => onOpenChange(false)}>
          <Button.Text>{t('::Close')}</Button.Text>
        </Button>
      </Column>
    );
  };

  const renderSyncComplete = () => {
    return (
      <Center
        transition={'medium'}
        animateOnly={['opacity', 'transform']}
        enterStyle={{
          o: 0,
          y: -20
        }}
        exitStyle={{
          o: 0,
          y: -20
        }}
      >
        <Check size={'$10'} strokeWidth={3} color={'$success'} />
        <Paragraph size={'$6'}>{bankId.statusText}</Paragraph>
      </Center>
    );
  };

  const renderBankIDThisDevice = () => {
    switch (bankId.status) {
      case BankIdStatus.Pending:
        return (
          <Column mt={'$3'} gap={'$3'}>
            <Pulse>
              <Image size={'$12'} mx={'auto'} source={themedBankIdLogo} contentFit={'contain'} />
            </Pulse>
            <Row gap={'$3'} my={'$3'} justifyContent={'center'} alignItems={'center'}>
              <Paragraph size={'$6'}>{bankId.statusText}</Paragraph>
              <Spinner color={'$bodyFont'} />
            </Row>

            {bankId.autoStartToken ? (
              <>
                <Button onPress={() => redirectToBankID(bankId.autoStartToken!, redirectPath)}>
                  <Button.Icon>
                    <Image size={'$1.5'} source={bankIdDark} />
                  </Button.Icon>
                  <Button.Text>
                    {t('::ExternalAccounts:Authenticate:BankIdMode:ThisDevice')}
                  </Button.Text>
                </Button>
                <Button variant={'outlined'} onPress={switchBankIdMode}>
                  <Button.Icon>
                    <Image size={'$1.5'} source={themedBankIdLogo} />
                  </Button.Icon>
                  <Button.Text>
                    {t('::ExternalAccounts:Authenticate:BankIdMode:OtherDevice')}
                  </Button.Text>
                </Button>
              </>
            ) : null}

            <ExternalAccountsTermsAndConditions />
          </Column>
        );

      case BankIdStatus.Failed:
        return renderSyncFailed();

      case BankIdStatus.Complete:
        return renderSyncComplete();
    }
  };

  const renderBankIDOtherDevice = () => {
    switch (bankId.status) {
      case BankIdStatus.Pending:
        return (
          <Column mt={'$3'} gap={'$3'}>
            {bankId.qrCode ? (
              <>
                <YGroup gap={'$3'}>
                  {['1', '2', '3'].map(step => (
                    <YGroup.Item key={step}>
                      <Row alignItems={'flex-start'} gap={'$3'}>
                        <Paragraph size={'$7'}>{step + '.'}</Paragraph>
                        <Highlighter
                          textToHighlight={t(`::Import:Authenticate:BankID:Native:Step${step}`)}
                          searchWords={[
                            t(`::Import:Authenticate:BankID:Native:Step${step}:Highlight`)
                          ]}
                          style={{ fontSize: fontSize6 }}
                        />
                      </Row>
                    </YGroup.Item>
                  ))}
                </YGroup>

                <Center my={'$4'}>
                  {/* Some providers can use a base64 encoded image instead of the special bankid link. This should rendered directly in an Image instead of the QRCode */}
                  {isBankIdLink(bankId.qrCode) ? (
                    <QRCode value={bankId.qrCode} />
                  ) : (
                    <Image size={'$20'} source={bankId.qrCode} />
                  )}
                </Center>
              </>
            ) : null}

            <Button onPress={switchBankIdMode}>
              <Image size={'$1.5'} source={bankIdDark} />
              <Button.Text>
                {t('::ExternalAccounts:Authenticate:BankIdMode:ThisDevice')}
              </Button.Text>
            </Button>

            <ExternalAccountsTermsAndConditions />
          </Column>
        );

      case BankIdStatus.Failed:
        return renderSyncFailed();

      case BankIdStatus.Complete:
        return renderSyncComplete();
    }
  };

  return (
    <FullScreenSheet
      ref={ref}
      open={open}
      onOpenChange={onOpenChange}
      headerIcon={<Image rounded size={'$2.5'} source={provider.imageUri} />}
      headerTitle={provider.displayName!}
      {...props}
    >
      {bankIdMode === BankIDMode.ThisDevice ? renderBankIDThisDevice() : renderBankIDOtherDevice()}
    </FullScreenSheet>
  );
};

AuthenticateExternalAccountModal.displayName = 'AuthenticateExternalAccountModal';
